import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import PortIcon from '@mui/icons-material/SendToMobileOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import Modal from 'components/Modal/Modal';
import { EditIcon } from 'components/icons';
import useRequest from 'hooks/useRequest';
import BuyNumberModal from 'modules/phoneapp/components/BuyNumberModal';
import PortNumberModal from 'modules/phoneapp/components/PortNumberModal';
import HostNumber from 'modules/phoneapp/components/phoneNumbers/HostNumber';
import MoveNumber from 'modules/phoneapp/components/phoneNumbers/MoveNumber';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { getOperators } from 'services/ivr.service';
import { formatPhoneNumber, getName } from 'utils';
import toast from 'utils/toast';
import AddConnectionModal from '../AddConnectionModal';
import EditConnForm from './EditConnForm';

const MODAL_TYPES = {
  MOVE: 'move',
  HOST: 'host',
  PORT: 'port',
  NONE: null,
};

const Connection = ({ connection: c, isAdmin, editing, onEdit, operators }) => {
  const [t] = useTranslation('settings');
  const forwardingTo = [];
  if (c.digital_receptionist?.digital_receptionist_name) {
    forwardingTo.push(c.digital_receptionist.digital_receptionist_name);
  }
  if (c.human_receptionist_contact_id !== '0') {
    const user = operators?.find(
      (u) => u.contact_id === c.human_receptionist_contact_id,
    );
    if (user) {
      forwardingTo.push(user.name);
    }
  }

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
      }}
    >
      <Box p={2}>
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              color="textSecondary"
              fontWeight={600}
              variant="caption"
            >
              {c.connection_name}
            </Typography>
            {isAdmin && !(c === editing) && (
              <IconButton
                icon={<EditIcon />}
                onClick={() => onEdit(c)}
                edge="end"
                size="small"
              />
            )}
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" mb={1}>
            <Typography>{formatPhoneNumber(c.conn_address)}</Typography>
            {forwardingTo.length ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneForwardedIcon
                  fontSize="inherit"
                  sx={{ color: 'text.secondary' }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Call forwarding
                </Typography>
                <Typography>{forwardingTo.join(', ')}</Typography>
              </Stack>
            ) : null}
          </Stack>
          <>
            {c.digital_receptionist_id === '0' &&
              c.human_receptionist_contact_id === '0' && (
                <Typography color="textSecondary">
                  {t(
                    'call-forwarding-disabled',
                    'Not forwarding incoming calls',
                  )}
                </Typography>
              )}
          </>
        </>
      </Box>
    </Card>
  );
};

const Connections = ({}) => {
  const [t] = useTranslation('settings');
  const user = useSelector((state) => state.user.user);
  const account = useSelector((state) => state.settings.account);
  const [editing, setEditing] = useState();
  const history = useHistory();
  const [operators] = useRequest(getOperators, {
    account_id: account.account_id,
  });
  const isAdmin = user.users_record_is_admin === '1';

  const updateConnection = () => {
    setEditing();
    toast.success(t('connection-updated', 'Settings saved!'));
  };

  const onAddConnection = () => {
    const { plan, counts } = account.usage;
    if (parseInt(counts.connections) >= parseInt(plan.connections)) {
      history.push('/settings/phone-numbers/buy');
    } else {
      history.push('/settings/phone-numbers/add');
    }
  };

  const [activeModal, setActiveModal] = useState(MODAL_TYPES.NONE);

  const closeModal = () => setActiveModal(MODAL_TYPES.NONE);

  const renderModal = () => {
    if (activeModal === MODAL_TYPES.MOVE) {
      return (
        <MoveNumber
          onPort={() => setActiveModal(MODAL_TYPES.PORT)}
          onHost={() => setActiveModal(MODAL_TYPES.HOST)}
          onClose={closeModal}
        />
      );
    }

    if (activeModal === MODAL_TYPES.HOST) {
      return <HostNumber onClose={closeModal} />;
    }

    if (activeModal === MODAL_TYPES.PORT) {
      return <PortNumberModal onClose={closeModal} />;
    }

    return null;
  };

  return (
    <Box>
      {isAdmin && (
        <Box
          textAlign="right"
          mb={2}
          gap={1}
          display="flex"
          justifyContent="flex-end"
        >
          <Button
            onClick={() => setActiveModal(MODAL_TYPES.MOVE)}
            startIcon={<PortIcon />}
            variant="outlined"
          >
            {t('use-my-existing-number', 'Use My Existing Number')}
          </Button>
          <Button onClick={onAddConnection}>{t('add-new', 'Add new')}</Button>
        </Box>
      )}
      {account.connections?.length
        ? account.connections.map((c) => (
            <Connection
              isAdmin={isAdmin}
              key={c.conn_id}
              connection={c}
              editing={editing}
              onEdit={setEditing}
              operators={operators}
            />
          ))
        : null}
      <Switch>
        <Route
          path="/settings/phone-numbers/add"
          component={AddConnectionModal}
        />
        <Route path="/settings/phone-numbers/buy">
          <BuyNumberModal
            onSuccess={() => history.push('/settings/phone-numbers/add')}
            onClose={() => history.goBack()}
          />
        </Route>
      </Switch>
      {editing && (
        <Modal
          fullWidth
          disableContentWrapper
          maxWidth="sm"
          title="Edit connection"
          onClose={() => setEditing()}
        >
          <EditConnForm
            connection={editing}
            onSave={updateConnection}
            onCancel={() => setEditing()}
          />
        </Modal>
      )}
      {renderModal()}
    </Box>
  );
};

export default Connections;
